import type { JSX } from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";

// A 6-digit code field: one real input (so paste, iOS code autofill and
// screen readers work) drawn as six boxes.
export const CodeInput = ({
  id,
  value,
  onChange,
  length = 6,
  disabled,
  autoFocus,
  invalid,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  invalid?: boolean;
}): JSX.Element => {
  const [focused, setFocused] = useState(false);
  const active = Math.min(value.length, length - 1);
  return (
    <div className="relative">
      <input
        // biome-ignore lint/a11y/noAutofocus: the code step exists only to type the code
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        autoComplete="one-time-code"
        className="absolute inset-0 z-10 h-full w-full cursor-text appearance-none bg-transparent text-[16px] text-transparent caret-transparent opacity-0 outline-hidden selection:bg-transparent"
        disabled={disabled}
        id={id}
        inputMode="numeric"
        maxLength={length}
        pattern="[0-9]*"
        required
        spellCheck={false}
        type="text"
        value={value}
        onBlur={() => setFocused(false)}
        onChange={(event) =>
          onChange(event.target.value.replace(/\D/g, "").slice(0, length))
        }
        onFocus={() => setFocused(true)}
      />
      <div
        aria-hidden
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
      >
        {Array.from({ length }, (_, index) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed positions
            key={index}
            className={cn(
              "flex h-14 items-center justify-center rounded-xl bg-v3-surface-2 font-v3-display text-[24px] font-semibold tabular-nums text-v3-text shadow-v3-hairline transition-[background-color,box-shadow]",
              focused &&
                index === active &&
                "bg-v3-panel ring-2 ring-v3-brand-ring",
              invalid && "shadow-[inset_0_0_0_1px_var(--v3-rec)]",
              disabled && "opacity-60",
            )}
          >
            {value[index] ??
              (focused && index === active ? (
                <span className="h-6 w-px animate-pulse bg-v3-text" />
              ) : null)}
          </div>
        ))}
      </div>
    </div>
  );
};
